export const GROWTH_LEVER_IDS = ['loanGrowthPts', 'depositGrowthPts'];
export const QUARTER_RATE_MIN = -15;
export const QUARTER_RATE_MAX = 15;

export const SEASONALITY_PRESETS = [
  { id: 'even', label: 'Even', multipliers: [1, 1, 1, 1] },
  { id: 'front', label: 'Front-weighted', multipliers: [1.6, 1.2, 0.8, 0.4] },
  { id: 'back', label: 'Back-weighted', multipliers: [0.4, 0.8, 1.2, 1.6] }
];

export const DEFAULT_QUARTERLY_GROWTH = Object.fromEntries(
  GROWTH_LEVER_IDS.map((leverId) => [leverId, {}])
);

const finiteOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const clampQuarterRate = (value) => Math.max(QUARTER_RATE_MIN, Math.min(QUARTER_RATE_MAX, value));

export const normalizeQuarterConfig = (config = {}) => ({
  preset: SEASONALITY_PRESETS.some((preset) => preset.id === config.preset)
    ? config.preset
    : 'even',
  preserveAnnual: config.preserveAnnual !== false,
  annualTarget: finiteOrNull(config.annualTarget),
  overrides: Array.from({ length: 4 }, (_, index) => finiteOrNull(config.overrides?.[index]))
});

export const normalizeQuarterlyGrowth = (saved = {}) => Object.fromEntries(
  GROWTH_LEVER_IDS.map((leverId) => [
    leverId,
    Object.fromEntries(
      Object.entries(saved?.[leverId] || {}).map(([period, config]) => [
        period,
        normalizeQuarterConfig(config)
      ])
    )
  ])
);

export const remainingYearQuarterMonthCounts = (latestActualMonth) => {
  const month = Math.max(0, Math.min(12, Number(latestActualMonth) || 0));
  return [0, 1, 2, 3].map((quarterIndex) => {
    const quarterStart = quarterIndex * 3 + 1;
    const quarterEnd = quarterStart + 2;
    if (month >= quarterEnd) return 0;
    return quarterEnd - Math.max(month, quarterStart - 1);
  });
};

const factorForRate = (annualizedRate, months) =>
  Math.pow(1 + (annualizedRate / 100 / 12), months);

const solveBaseRate = ({ targetFactor, fixedFactor, multipliers, monthCounts, openIndexes }) => {
  if (!openIndexes.length) return 0;
  const factorAt = (baseRate) => openIndexes.reduce(
    (factor, index) => factor * factorForRate(baseRate * multipliers[index], monthCounts[index]),
    fixedFactor
  );
  let low = -100;
  let high = 100;
  for (let iteration = 0; iteration < 40; iteration += 1) {
    if (high - low < 1e-10) break;
    const mid = (low + high) / 2;
    if (factorAt(mid) < targetFactor) low = mid;
    else high = mid;
  }
  return (low + high) / 2;
};

/**
 * Resolves annualized quarterly adjustment rates. Preserved targets tie out by
 * compounding, rather than dividing an annual rate by four.
 */
export const resolveQuarterValues = ({
  annualTarget = 0,
  config,
  monthCounts = [3, 3, 3, 3]
}) => {
  const normalized = normalizeQuarterConfig(config);
  const preset = SEASONALITY_PRESETS.find((item) => item.id === normalized.preset)
    || SEASONALITY_PRESETS[0];
  const target = Number(annualTarget) || 0;
  const activeIndexes = monthCounts
    .map((count, index) => (count > 0 ? index : null))
    .filter((index) => index !== null);
  const overriddenIndexes = activeIndexes.filter((index) => normalized.overrides[index] !== null);
  const generatedIndexes = activeIndexes.filter((index) => normalized.overrides[index] === null);
  const activeMonthCount = activeIndexes.reduce((sum, index) => sum + monthCounts[index], 0);
  const targetFactor = factorForRate(target, activeMonthCount);
  const fixedFactor = overriddenIndexes.reduce(
    (factor, index) => factor * factorForRate(normalized.overrides[index], monthCounts[index]),
    1
  );
  const baseRate = solveBaseRate({
    targetFactor,
    fixedFactor: normalized.preserveAnnual ? fixedFactor : 1,
    multipliers: preset.multipliers,
    monthCounts,
    openIndexes: normalized.preserveAnnual ? generatedIndexes : activeIndexes
  });
  const generated = preset.multipliers.map((multiplier) => baseRate * multiplier);

  return monthCounts.map((count, index) => {
    if (count <= 0) return null;
    if (normalized.overrides[index] !== null) return clampQuarterRate(normalized.overrides[index]);
    return clampQuarterRate(generated[index]);
  });
};

export const resolveForecastQuarterAdjustment = ({
  quarterlyGrowth,
  leverId,
  year,
  quarterIndex,
  annualTarget
}) => {
  // Full forecast years use numeric keys here. RoY intentionally resolves via
  // resolveQuarterValues with partial-quarter month counts in the bridge path.
  if (String(year) === 'roy') {
    throw new TypeError('RoY adjustments require resolveQuarterValues with remainder month counts');
  }
  const config = quarterlyGrowth?.[leverId]?.[String(year)];
  if (!config) return Number(annualTarget) || 0;
  const values = resolveQuarterValues({ annualTarget, config });
  return Number(values[quarterIndex]) || 0;
};

export const effectiveAnnualizedRate = (values, monthCounts = [3, 3, 3, 3]) => {
  const activeMonths = monthCounts.reduce((sum, count) => sum + count, 0);
  if (!activeMonths) return null;
  const factor = values.reduce((product, value, index) => (
    value === null || monthCounts[index] <= 0
      ? product
      : product * factorForRate(Number(value) || 0, monthCounts[index])
  ), 1);
  return (Math.pow(factor, 1 / activeMonths) - 1) * 1200;
};

export const summarizeQuarterlyGrowthProfile = ({
  configs = {},
  annualTargets = [],
  remainderYear = 2026,
  latestActualMonth = 0
}) => {
  const periods = ['roy', ...annualTargets.map((_, index) => String(remainderYear + 1 + index))];
  const details = Object.fromEntries(periods.map((period) => {
    const rawConfig = configs?.[period];
    const config = normalizeQuarterConfig(rawConfig);
    const monthCounts = period === 'roy'
      ? remainingYearQuarterMonthCounts(latestActualMonth)
      : [3, 3, 3, 3];
    const target = period === 'roy'
      ? config.annualTarget ?? 0
      : Number(annualTargets[Number(period) - (remainderYear + 1)]) || 0;
    const values = resolveQuarterValues({ annualTarget: target, config, monthCounts });
    const effectiveRate = effectiveAnnualizedRate(values, monthCounts);
    const activeIndexes = monthCounts
      .map((count, index) => (count > 0 ? index : null))
      .filter((index) => index !== null);
    const hasActiveOverrides = activeIndexes.some((index) => config.overrides[index] !== null);
    const configured = activeIndexes.length > 0 && Boolean(rawConfig) && (
      config.preset !== 'even'
      || config.preserveAnnual === false
      || hasActiveOverrides
      || (period === 'roy' && target !== 0)
    );
    const needsAttention = configured && config.preserveAnnual && effectiveRate !== null
      && Math.abs(effectiveRate - target) >= 0.0001;
    return [period, { configured, needsAttention, effectiveRate, target, preserveAnnual: config.preserveAnnual }];
  }));
  return {
    details,
    configuredCount: Object.values(details).filter((detail) => detail.configured).length,
    issueCount: Object.values(details).filter((detail) => detail.needsAttention).length
  };
};

export const hasQuarterlyGrowthChanges = (quarterlyGrowth = {}, latestActualMonth = 0) =>
  GROWTH_LEVER_IDS.some((leverId) => Object.entries(quarterlyGrowth?.[leverId] || {}).some(
    ([period, rawConfig]) => {
      const config = normalizeQuarterConfig(rawConfig);
      const activeQuarterIndexes = period === 'roy'
        ? remainingYearQuarterMonthCounts(latestActualMonth)
            .map((count, index) => (count > 0 ? index : null))
            .filter((index) => index !== null)
        : [0, 1, 2, 3];
      if (activeQuarterIndexes.length === 0) return false;
      return config.preset !== 'even'
        || config.preserveAnnual === false
        || activeQuarterIndexes.some((index) => config.overrides[index] !== null)
        || (period === 'roy' && config.annualTarget !== null && config.annualTarget !== 0);
    }
  ));

export const describeQuarterlyGrowthConfig = (value) => {
  if (!value) return 'Annual profile';
  const overrides = (value.overrides || [])
    .map((entry, index) => (
      entry === null || entry === undefined ? null : `Q${index + 1} ${entry} pts`
    ))
    .filter(Boolean)
    .join(', ');
  return [
    `preset ${value.preset || 'even'}`,
    `target ${value.annualTarget ?? 'annual lever'} pts`,
    value.preserveAnnual === false ? 'annual target redefined' : 'annual target preserved',
    overrides || 'no quarter overrides'
  ].join(' · ');
};
